import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowLeft, FaShoppingCart, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useCart } from '../context/CartContext';
import { sanitizeHTML } from '../utils/sanitize';

// Components
import Button from '../components/Button';
import Spinner from '../components/Spinner';

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const productDoc = doc(db, 'products', id);
        const productSnapshot = await getDoc(productDoc);
        
        if (productSnapshot.exists()) {
          setProduct({
            id: productSnapshot.id,
            ...productSnapshot.data()
          });
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= (product?.quantity || 1)) {
      setQuantity(value);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const incrementQuantity = () => {
    if (quantity < (product?.quantity || 1)) {
      setQuantity(quantity + 1);
    }
  };

  const handleAddToCart = () => {
    if (!product || isOutOfStock) return;
    
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      imageUrl: product.images?.[0] || product.image || '',
      quantity: quantity,
      stock: stockQuantity
    });
    
    // Show feedback to user
    alert(`${product.name} added to cart!`);
  };

  const nextImage = () => {
    if (!product || !product.images || product.images.length <= 1) return;
    setCurrentImageIndex((prevIndex) => 
      prevIndex === product.images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    if (!product || !product.images || product.images.length <= 1) return;
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? product.images.length - 1 : prevIndex - 1
    );
  };

  const selectThumbnail = (index) => {
    setCurrentImageIndex(index);
  };

  const handleMouseMove = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomPosition({ x, y });
  };

  const handleMouseEnter = () => {
    setIsZoomed(true);
  };

  const handleMouseLeave = () => {
    setIsZoomed(false);
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner />
        <p>Loading product details...</p>
      </LoadingContainer>
    );
  }

  if (error || !product) {
    return (
      <ErrorContainer>
        <h2>Product Not Found</h2>
        <p>{error || "The product you're looking for doesn't exist or has been removed."}</p>
        <Button onClick={() => navigate('/categories')}>
          Back to Products
        </Button>
      </ErrorContainer>
    );
  }

  // Check if product is in stock and has quantity
  // Check both 'stock' and 'quantity' for backward compatibility
  const stockQuantity = product.stock || product.quantity || 0;
  const isOutOfStock = stockQuantity <= 0;

  return (
    <PageContainer>
      <Container>
        <BackButton onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </BackButton>

        <ProductContainer>
          <ProductImageContainer>
            <ImageWrapper
              onMouseMove={handleMouseMove}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <ProductImage 
                src={
                  product.images && product.images.length > 0
                    ? product.images[currentImageIndex]
                    : product.image || 'https://via.placeholder.com/300x200?text=Product'
                } 
                alt={product.name}
                $isZoomed={isZoomed}
                style={{
                  transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`
                }}
              />
              {isZoomed && (
                <ZoomOverlay>
                  <ZoomText>Hover to zoom • Click to view full size</ZoomText>
                </ZoomOverlay>
              )}
            </ImageWrapper>
            {product.featured && <Badge>Featured</Badge>}
            {isOutOfStock && <Badge $outOfStock>Out of Stock</Badge>}
            
            {product.images && product.images.length > 1 && (
              <>
                <ImageNavButton $left onClick={prevImage}>
                  <FaChevronLeft />
                </ImageNavButton>
                <ImageNavButton $right onClick={nextImage}>
                  <FaChevronRight />
                </ImageNavButton>
                <ThumbnailsContainer>
                  {product.images.map((img, index) => (
                    <Thumbnail 
                      key={index}
                      $active={index === currentImageIndex}
                      onClick={() => selectThumbnail(index)}
                    >
                      <img src={img} alt={`${product.name} thumbnail ${index + 1}`} />
                    </Thumbnail>
                  ))}
                </ThumbnailsContainer>
              </>
            )}
          </ProductImageContainer>

          <ProductDetails>
            <ProductCategory>{product.category}</ProductCategory>
            <ProductName>{product.name}</ProductName>
            <ProductPrice>${parseFloat(product.price).toFixed(2)}</ProductPrice>
            
            <ProductDescription dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.description) }} />
            
            <ProductMeta>
              {product.weight && (
                <MetaItem>
                  <MetaLabel>Weight:</MetaLabel>
                  <MetaValue>{product.weight}</MetaValue>
                </MetaItem>
              )}
              
              {product.dimensions && (
                <MetaItem>
                  <MetaLabel>Dimensions:</MetaLabel>
                  <MetaValue>{product.dimensions}</MetaValue>
                </MetaItem>
              )}
              
              {product.material && (
                <MetaItem>
                  <MetaLabel>Material:</MetaLabel>
                  <MetaValue>{product.material}</MetaValue>
                </MetaItem>
              )}
              
              {product.condition && (
                <MetaItem>
                  <MetaLabel>Condition:</MetaLabel>
                  <MetaValue>{product.condition}</MetaValue>
                </MetaItem>
              )}
              
              {product.origin && (
                <MetaItem>
                  <MetaLabel>Origin:</MetaLabel>
                  <MetaValue>{product.origin}</MetaValue>
                </MetaItem>
              )}
              
              {product.year && (
                <MetaItem>
                  <MetaLabel>Year:</MetaLabel>
                  <MetaValue>{product.year}</MetaValue>
                </MetaItem>
              )}
            </ProductMeta>
            
            <StockStatus $inStock={!isOutOfStock}>
              {!isOutOfStock ? `In Stock: ${stockQuantity}` : 'Out of Stock'}
            </StockStatus>
            
            {!isOutOfStock && (
              <QuantityContainer>
                <QuantityLabel>Quantity:</QuantityLabel>
                <QuantityControls>
                  <QuantityButton onClick={decrementQuantity} disabled={quantity <= 1}>-</QuantityButton>
                  <QuantityInput
                    type="number"
                    min="1"
                    max={stockQuantity}
                    value={quantity}
                    onChange={handleQuantityChange}
                  />
                  <QuantityButton onClick={incrementQuantity} disabled={quantity >= stockQuantity}>+</QuantityButton>
                </QuantityControls>
              </QuantityContainer>
            )}
            
            <ActionButtons>
              <Button 
                variant="primary" 
                size="large" 
                icon={<FaShoppingCart />}
                iconPosition="left"
                onClick={handleAddToCart}
                disabled={isOutOfStock}
              >
                {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
              </Button>
            </ActionButtons>
          </ProductDetails>
        </ProductContainer>
      </Container>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding: 120px 0 80px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.colors.text.dark};
  font-weight: 500;
  margin-bottom: 2rem;
  transition: color ${({ theme }) => theme.transitions.short};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ProductContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ProductImageContainer = styled.div`
  flex: 1;
  position: relative;
  margin-right: 2rem;
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 2rem;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  background-color: #f9f9f9;
  cursor: zoom-in;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: contain;
  border-radius: 8px;
  background-color: #f9f9f9;
  transition: transform 0.3s ease;
  transform: ${props => props.$isZoomed ? 'scale(2)' : 'scale(1)'};
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const ZoomOverlay = styled.div`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.7);
  padding: 8px 16px;
  border-radius: 4px;
  pointer-events: none;
  z-index: 10;
`;

const ZoomText = styled.span`
  color: white;
  font-size: 0.875rem;
  white-space: nowrap;
`;

const ImageNavButton = styled.button`
  position: absolute;
  top: 200px;
  transform: translateY(-50%);
  ${props => props.$left ? 'left: 10px;' : ''}
  ${props => props.$right ? 'right: 10px;' : ''}
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.9);
  border: 2px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  &:hover {
    background-color: rgba(255, 255, 255, 1);
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  svg {
    color: #333;
    font-size: 1.2rem;
  }
  
  @media (max-width: 768px) {
    top: 150px;
  }
`;

const ThumbnailsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 15px;
`;

const Thumbnail = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${props => props.$active ? '#C41E3A' : 'transparent'};
  opacity: ${props => props.$active ? 1 : 0.7};
  transition: all 0.2s;
  
  &:hover {
    opacity: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Badge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: ${props => props.$outOfStock ? '#666' : '#C41E3A'};
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  z-index: 1;
`;

const ProductDetails = styled.div``;

const ProductCategory = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ProductName = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text.dark};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const ProductPrice = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.75rem;
  }
`;

const ProductDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const ProductMeta = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
`;

const MetaLabel = styled.span`
  font-weight: 600;
  margin-right: 0.5rem;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const MetaValue = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

const StockStatus = styled.div`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-weight: 600;
  margin-bottom: 1.5rem;
  background-color: ${({ $inStock, theme }) => 
    $inStock ? theme.colors.success + '20' : theme.colors.error + '20'};
  color: ${({ $inStock, theme }) => 
    $inStock ? theme.colors.success : theme.colors.error};
`;

const QuantityContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
`;

const QuantityLabel = styled.span`
  font-weight: 600;
  margin-right: 1rem;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
`;

const QuantityButton = styled.button`
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  color: #333;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  
  &:first-of-type {
    border-radius: 4px 0 0 4px;
  }
  
  &:last-of-type {
    border-radius: 0 4px 4px 0;
  }
  
  &:hover {
    background-color: #e5e5e5;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 50px;
  height: 30px;
  border: 1px solid #ddd;
  text-align: center;
  font-size: 1rem;
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
`;

const ErrorContainer = styled.div`
  max-width: 600px;
  margin: 100px auto;
  text-align: center;
  padding: 2rem;
  
  h2 {
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.colors.error};
  }
  
  p {
    margin-bottom: 2rem;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

export default ProductPage;
